/* Wandercat – Interaktionen für die Marketing-Sections.
   Alle init-Funktionen sind idempotent und werden bei Theme-Editor-Events erneut ausgeführt. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  const once = (el, key) => {
    if (el.dataset[key]) return false;
    el.dataset[key] = 'true';
    return true;
  };

  /* --- Reveal beim Scrollen ------------------------------------------- */
  let revealObserver = null;

  function initReveal(root) {
    const items = root.querySelectorAll('.wc-reveal:not(.is-visible)');
    if (!items.length) return;

    if (designMode || reduceMotion.matches || typeof IntersectionObserver === 'undefined') {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.12 },
      );
    }

    items.forEach((el) => revealObserver.observe(el));
  }

  /* --- Kopfzeile -------------------------------------------------------- */
  function initHeader(root) {
    const header = root.querySelector('[data-wc-header]');
    if (!header || !once(header, 'wcInit')) return;

    const navWrap = header.querySelector('[data-wc-navwrap]');
    const toggle = header.querySelector('[data-wc-nav-toggle]');
    const mobileNav = header.querySelector('[data-wc-mobile-nav]');

    const syncScrolled = () => {
      if (navWrap) navWrap.classList.toggle('is-scrolled', window.scrollY > 8);
    };
    syncScrolled();
    window.addEventListener('scroll', syncScrolled, { passive: true });

    const syncHeight = () => {
      const section = header.closest('.shopify-section') || header;
      document.documentElement.style.setProperty('--wc-header-h', `${section.offsetHeight}px`);
    };
    syncHeight();
    window.addEventListener('resize', syncHeight);
    if ('ResizeObserver' in window) new ResizeObserver(syncHeight).observe(header);

    if (toggle && mobileNav) {
      const setOpen = (open) => {
        toggle.setAttribute('aria-expanded', String(open));
        toggle.setAttribute('aria-label', open ? toggle.dataset.labelClose : toggle.dataset.labelOpen);
        mobileNav.hidden = !open;
      };
      toggle.addEventListener('click', () => setOpen(mobileNav.hidden));
      mobileNav.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setOpen(false)));
    }
  }

  /* --- Mobiler Sticky-Button ------------------------------------------- */
  function initMobileCta() {
    const bar = document.querySelector('[data-wc-mobile-cta]');
    if (!bar || !once(bar, 'wcInit')) return;
    if (typeof IntersectionObserver === 'undefined') return;

    let pastHero = false;
    let blocked = false;
    const link = bar.querySelector('a');

    const sync = () => {
      const show = pastHero && !blocked;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      if (link) link.tabIndex = show ? 0 : -1;
    };

    const hero = document.getElementById('top');
    if (hero) {
      new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          pastHero = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          sync();
        },
        { threshold: 0 },
      ).observe(hero);
    }

    const targetId = bar.dataset.target;
    const hideTargets = [
      targetId ? document.getElementById(targetId) : null,
      ...Array.from(document.querySelectorAll('[data-mobile-cta-hide]')),
    ].filter(Boolean);

    const visible = new Set();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        });
        blocked = visible.size > 0;
        sync();
      },
      { threshold: 0.08 },
    );
    hideTargets.forEach((el) => io.observe(el));
    sync();
  }

  /* --- Hero-Video: erst nach dem Seiten-Load laden ----------------------
     Das Poster-<img> ist der erste sichtbare Inhalt (LCP). Das Video wird erst
     geladen, wenn die Seite fertig ist (Handy: kleinere Datei), und blendet beim
     ersten abgespielten Frame über das deckungsgleiche Poster ein – ohne CLS.
     Reduzierte Bewegung oder Datensparmodus: kein Video, nur das Poster. */
  function initHero(root) {
    root.querySelectorAll('[data-wc-hero-video]').forEach((video) => {
      if (!once(video, 'wcInit')) return;
      const media = video.closest('[data-wc-hero-media]');
      const saveData = Boolean(navigator.connection && navigator.connection.saveData);
      let loaded = false;

      const load = () => {
        if (loaded || reduceMotion.matches || saveData) return;
        loaded = true;
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        const src = (mobile ? video.dataset.srcMobile : video.dataset.srcDesktop) || video.dataset.srcDesktop;
        if (!src) return;
        video.addEventListener('playing', () => media && media.classList.add('is-playing'));
        video.src = src;
        video.play().catch(() => {});
      };

      const schedule = () => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(load, { timeout: 2500 });
        else window.setTimeout(load, 300);
      };
      if (document.readyState === 'complete') schedule();
      else window.addEventListener('load', schedule, { once: true });

      reduceMotion.addEventListener('change', () => {
        if (reduceMotion.matches) {
          video.pause();
          if (media) media.classList.remove('is-playing');
        } else if (loaded) {
          video.play().catch(() => {});
        } else {
          load();
        }
      });
    });
  }

  /* --- App-Tabs --------------------------------------------------------
     Ein Reiter schaltet Beschreibung und Screen gemeinsam um. Der Screen liegt
     im Telefon, zu dem die Funktion gehört (data-wc-tab-phone-ref); dieses
     Telefon wird aktiv (mobil: als einziges sichtbar, Desktop: nicht abgeblendet). */
  function initAppTabs(root) {
    root.querySelectorAll('[data-wc-tabs]').forEach((wrap) => {
      if (!once(wrap, 'wcInit')) return;
      const tabs = Array.from(wrap.querySelectorAll('[role="tab"]'));
      const panels = Array.from(wrap.querySelectorAll('[data-wc-tab-panel]'));
      const phones = Array.from(wrap.querySelectorAll('[data-wc-tab-phone]'));

      const select = (tab, focus) => {
        const id = tab.dataset.wcTab;
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
        });
        panels.forEach((panel) => (panel.hidden = panel.dataset.wcTabPanel !== id));
        phones.forEach((phone) => {
          const on = phone.dataset.wcTabPhone === tab.dataset.wcTabPhoneRef;
          phone.classList.toggle('is-active', on);
          if (on) {
            phone.querySelectorAll('[data-wc-tab-screen]').forEach((screen) => {
              screen.hidden = screen.dataset.wcTabScreen !== id;
            });
          }
        });
        if (focus) tab.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(tab, false));
        tab.addEventListener('keydown', (event) => {
          const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
          let next = null;
          if (step) next = tabs[(index + step + tabs.length) % tabs.length];
          else if (event.key === 'Home') next = tabs[0];
          else if (event.key === 'End') next = tabs[tabs.length - 1];
          if (!next) return;
          event.preventDefault();
          select(next, true);
        });
      });
    });
  }

  /* --- Spline-3D-Explorer: Lazy-Load per IntersectionObserver ------------
     Das Viewer-Script und die <spline-viewer>-Szene werden erst angefordert,
     wenn der Rahmen tatsächlich in den Viewport scrollt (plus 200px Vorlauf).
     Bis dahin steht ein Platzhalterbild + "Lädt …"-Label.
     Die Szene braucht WebGPU (Safari ohne WebGPU zeigt sonst "This scene
     requires a WebGPU capable browser"). Deshalb wird vorab geprüft; ohne
     WebGPU, bei Ladefehler, Timeout oder einer Fehlermeldung des Viewers
     übernimmt der statische Fallback (Titelbild + Punkte + Info-Cards).
     Testen: ?wc_spline=static erzwingt den Fallback. ------------------- */
  /* Der Viewer (@splinetool/viewer 2.0.55 aus npm, Draco-Pfad auf die eigenen Assets
     gepatcht) liegt als Theme-Asset vor; die Section übergibt seine URL per
     data-spline-viewer. Kein Laden mehr von cdn.spline.design. */
  /* Subresource Integrity für assets/spline-viewer.webgpu.js. Wird die Datei ersetzt
     (Update oder neuer Patch), MUSS dieser Hash mit – sonst blockiert der Browser das
     Skript und es erscheint die statische Ansicht. Neu berechnen:
     openssl dgst -sha384 -binary assets/spline-viewer.webgpu.js | openssl base64 -A */
  const SPLINE_VIEWER_INTEGRITY = 'sha384-VjbwOMCbXO835UXR02kgg4YNrt3a3UEgodcG9GiUizs7vCiS4eUVGZ4nTk7L8oxP';
  const SPLINE_READY_GRACE_MS = 1000;
  const SPLINE_FAILURE_TIMEOUT_MS = 15000;
  const WEBGPU_CHECK_TIMEOUT_MS = 3000;
  const SPLINE_ERROR_PATTERN = /webgpu|not supported|requires/i;
  let splineScriptState = 'idle'; // idle | loading | loaded | error
  const splineScriptWaiters = [];
  let webgpuSupport = null;

  function hasWebGPU() {
    if (webgpuSupport) return webgpuSupport;
    webgpuSupport = new Promise((resolve) => {
      if (/[?&]wc_spline=static\b/.test(window.location.search)) return resolve(false);
      if (!window.isSecureContext || !navigator.gpu || typeof navigator.gpu.requestAdapter !== 'function') {
        return resolve(false);
      }
      const timer = window.setTimeout(() => resolve(false), WEBGPU_CHECK_TIMEOUT_MS);
      navigator.gpu.requestAdapter()
        .then((adapter) => resolve(Boolean(adapter)))
        .catch(() => resolve(false))
        .finally(() => window.clearTimeout(timer));
    });
    return webgpuSupport;
  }

  function loadSplineViewerScript(src, callback) {
    if (!src) return callback(false);
    if (window.customElements && window.customElements.get('spline-viewer')) {
      callback(true);
      return;
    }
    if (splineScriptState === 'loaded') return callback(true);
    if (splineScriptState === 'error') return callback(false);

    splineScriptWaiters.push(callback);
    if (splineScriptState === 'loading') return;

    splineScriptState = 'loading';
    const script = document.createElement('script');
    script.type = 'module';
    script.crossOrigin = 'anonymous';
    script.integrity = SPLINE_VIEWER_INTEGRITY;
    script.src = src;
    script.addEventListener('load', () => {
      splineScriptState = 'loaded';
      splineScriptWaiters.splice(0).forEach((fn) => fn(true));
    });
    script.addEventListener('error', () => {
      splineScriptState = 'error';
      splineScriptWaiters.splice(0).forEach((fn) => fn(false));
    });
    document.head.appendChild(script);
  }

  /* Das HTML-Overlay der Spline-Szene (Infokarten, Funktionsleiste) läuft in einem
     abgeschotteten Frame und kennt die Seitensprache nicht. Wir schicken sie ihm per
     postMessage – mehrfach, weil der Frame erst nach dem Laden der Szene existiert.
     Das Overlay antwortet mit { type: 'wc-lang-ack' }, dann hören wir auf. */
  function sendOverlayLanguage(viewer) {
    const lang = (document.documentElement.lang || 'de').slice(0, 2).toLowerCase();
    let acked = false;
    let tries = 0;
    const onAck = (event) => {
      if (event.data && event.data.type === 'wc-lang-ack') {
        acked = true;
        window.removeEventListener('message', onAck);
      }
    };
    window.addEventListener('message', onAck);
    const tick = () => {
      if (acked || tries++ > 40) return;
      const frame = viewer.shadowRoot && viewer.shadowRoot.querySelector('iframe');
      if (frame && frame.contentWindow) frame.contentWindow.postMessage({ type: 'wc-lang', lang }, '*');
      window.setTimeout(tick, 500);
    };
    tick();
  }

  function initStaticExplorer(container) {
    const triggers = Array.from(container.querySelectorAll('[data-wc-static-trigger]'));
    const cards = Array.from(container.querySelectorAll('[data-wc-static-card]'));
    let openId = null;

    const show = (id, focusCard) => {
      openId = id;
      cards.forEach((card) => { card.hidden = card.dataset.wcStaticCard !== id; });
      triggers.forEach((btn) => btn.setAttribute('aria-expanded', String(btn.dataset.wcStaticTrigger === id)));
      if (focusCard && id) {
        const close = container.querySelector(`[data-wc-static-card="${id}"] [data-wc-static-close]`);
        if (close) close.focus();
      }
    };

    triggers.forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.wcStaticTrigger;
        show(openId === id ? null : id, false);
      });
    });

    container.querySelectorAll('[data-wc-static-close]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = openId;
        show(null, false);
        const back = container.querySelector(`.wc-spline-static__step[data-wc-static-trigger="${id}"]`);
        if (back) back.focus();
      });
    });

    container.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !openId) return;
      const id = openId;
      show(null, false);
      const back = container.querySelector(`.wc-spline-static__step[data-wc-static-trigger="${id}"]`);
      if (back) back.focus();
    });
  }

  function initSplineExplorer(root) {
    root.querySelectorAll('[data-wc-spline-explorer]').forEach((frame) => {
      if (!once(frame, 'wcInit')) return;

      const stage = frame.querySelector('[data-wc-spline-stage]');
      const placeholder = frame.querySelector('[data-wc-spline-placeholder]');
      const staticView = frame.querySelector('[data-wc-spline-static]');
      const section = frame.closest('.wc-spline-explorer');
      const srList = section ? section.querySelector('[data-wc-spline-fallback]') : null;
      const url = frame.dataset.splineUrl;

      let settled = false;
      let viewer = null;

      const hidePlaceholder = () => {
        if (placeholder) placeholder.hidden = true;
      };

      /* Fallback: Viewer (samt eventueller Fehlermeldung) komplett entfernen,
         statische Ansicht zeigen. Ohne statische Ansicht wenigstens die Textliste. */
      const activateStatic = () => {
        settled = true;
        if (viewer) viewer.remove();
        if (stage) stage.replaceChildren();
        hidePlaceholder();
        frame.classList.add('is-static');
        if (staticView) {
          staticView.hidden = false;
          initStaticExplorer(staticView);
        } else if (srList) {
          srList.classList.remove('visually-hidden');
        }
      };

      if (!url || !stage || typeof IntersectionObserver === 'undefined' || !('customElements' in window)) {
        activateStatic();
        return;
      }

      /* Sicherheitsnetz: Meldet der Viewer trotz bestandener Prüfung einen
         Fehler im eigenen Shadow DOM, sofort auf den Fallback wechseln. */
      const watchForViewerError = () => {
        const started = Date.now();
        const check = () => {
          if (!viewer || !viewer.isConnected) return;
          const sr = viewer.shadowRoot;
          const text = sr ? Array.from(sr.querySelectorAll('#container, div, p, span')).map((el) => el.textContent).join(' ') : '';
          if (SPLINE_ERROR_PATTERN.test(text)) {
            activateStatic();
            return;
          }
          if (Date.now() - started < SPLINE_FAILURE_TIMEOUT_MS) window.setTimeout(check, 500);
        };
        check();
      };

      const mount = () => {
        viewer = document.createElement('spline-viewer');
        viewer.setAttribute('url', url);
        if (frame.dataset.splineLabel) viewer.setAttribute('aria-label', frame.dataset.splineLabel);
        /* Falls der Viewer doch ein 'load' feuert, übernehmen wir das sofort - ist aber
           kein verlässliches Signal (spline-viewer garantiert dieses Event nicht). */
        viewer.addEventListener('load', () => {
          if (settled) return;
          settled = true;
          hidePlaceholder();
        }, { once: true });
        viewer.addEventListener('error', () => activateStatic(), { once: true });
        stage.appendChild(viewer);

        loadSplineViewerScript(frame.dataset.splineViewer, (ok) => {
          if (!ok) {
            activateStatic();
            return;
          }
          /* Element ist registriert und im DOM - die Szene initialisiert sich intern
             (eigene srcdoc-Iframe), ohne dass wir ein Fertig-Event abwarten können.
             Kurze Karenzzeit statt endlosem Warten auf ein Event, das nie kommt. */
          window.customElements.whenDefined('spline-viewer').then(() => {
            watchForViewerError();
            sendOverlayLanguage(viewer);
            window.setTimeout(() => {
              if (settled) return;
              settled = true;
              hidePlaceholder();
            }, SPLINE_READY_GRACE_MS);
          });
        });

        window.setTimeout(() => {
          if (settled) return;
          activateStatic();
        }, SPLINE_FAILURE_TIMEOUT_MS);
      };

      const start = () => {
        hasWebGPU().then((supported) => {
          if (supported) mount();
          else activateStatic();
        });
      };

      if (designMode) {
        start();
        return;
      }

      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.disconnect();
          start();
        });
      }, { rootMargin: '200px 0px', threshold: 0.01 });
      io.observe(frame);
    });
  }

  /* --- FAQ-Akkordeon (nur eine Antwort je Gruppe offen) ----------------- */
  /* FAQ: immer nur eine Frage offen, weicher Höhen-Übergang beim Öffnen und
     Schließen. <details> bleibt die Grundlage (ohne JS klappt es sofort um). */
  const FAQ_DURATION = 280;
  const FAQ_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

  function animateFaq(details, open) {
    const answer = details.querySelector('.wc-faq__answer');
    if (details._wcAnim) details._wcAnim.cancel();
    if (!answer || reduceMotion.matches || typeof answer.animate !== 'function') {
      details.classList.remove('is-closing');
      details.open = open;
      return;
    }
    if (open) {
      details.open = true;
      const height = answer.offsetHeight;
      const padding = getComputedStyle(answer).paddingBottom;
      details._wcAnim = answer.animate(
        [{ height: '0px', paddingBottom: '0px', opacity: 0 }, { height: `${height}px`, paddingBottom: padding, opacity: 1 }],
        { duration: FAQ_DURATION, easing: FAQ_EASING }
      );
    } else {
      details.classList.add('is-closing');
      const height = answer.offsetHeight;
      const padding = getComputedStyle(answer).paddingBottom;
      const anim = answer.animate(
        [{ height: `${height}px`, paddingBottom: padding, opacity: 1 }, { height: '0px', paddingBottom: '0px', opacity: 0 }],
        { duration: FAQ_DURATION, easing: FAQ_EASING }
      );
      details._wcAnim = anim;
      anim.onfinish = () => {
        details.open = false;
        details.classList.remove('is-closing');
        details._wcAnim = null;
      };
    }
  }

  function initFaq(root) {
    root.querySelectorAll('[data-wc-faq-list]').forEach((list) => {
      if (!once(list, 'wcInit')) return;
      list.addEventListener('click', (event) => {
        const summary = event.target.closest('summary');
        if (!summary || !list.contains(summary)) return;
        const details = summary.parentElement;
        event.preventDefault();
        const opening = !details.open || details.classList.contains('is-closing');
        if (opening) {
          list.querySelectorAll('details[open]').forEach((other) => {
            if (other !== details && !other.classList.contains('is-closing')) animateFaq(other, false);
          });
        }
        animateFaq(details, opening);
      });
      /* Fallback, falls ein <details> anders geöffnet wird (z. B. Theme-Editor). */
      list.addEventListener('toggle', (event) => {
        const details = event.target;
        if (!(details instanceof HTMLDetailsElement) || !details.open) return;
        list.querySelectorAll('details[open]').forEach((other) => {
          if (other !== details && !other.classList.contains('is-closing')) other.open = false;
        });
      }, true);
    });
  }

  /* Elemente mit data-wc-inview bekommen .is-running, solange sie sichtbar sind.
     Ihre CSS-Animationen laufen nur mit dieser Klasse (spart Akku, ruhige Seite). */
  function initInView(root) {
    root.querySelectorAll('[data-wc-inview]').forEach((el) => {
      if (!once(el, 'wcInview')) return;
      if (!('IntersectionObserver' in window)) {
        el.classList.add('is-running');
        return;
      }
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => entry.target.classList.toggle('is-running', entry.isIntersecting));
      }, { threshold: 0.15 }).observe(el);
    });
  }

  /* --- Nach Formularversand zur Bestätigung scrollen -------------------- */
  function scrollToFormStatus() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('customer_posted')) return;
    document.querySelectorAll('[data-wc-form-status]').forEach((status) => {
      if (!status.querySelector('.wc-form__success')) return;
      const modal = status.closest('modal-dialog');
      if (modal && typeof modal.show === 'function') modal.show();
      else status.scrollIntoView({ behavior: 'auto', block: 'center' });
    });
  }

  /* --- Newsletter-Popup: Exit-Intent (Desktop) / Timer (Touch) ---------- */
  function initNewsletterPopup(root) {
    const popup = root.querySelector('[data-wc-popup]');
    if (!popup || !once(popup, 'wcInit')) return;
    if (window.Shopify && window.Shopify.designMode) return;

    const storageKey = 'wc-newsletter-popup-dismissed';
    const cooldownMs = 7 * 24 * 60 * 60 * 1000;
    try {
      const last = window.localStorage.getItem(storageKey);
      if (last && Date.now() - parseInt(last, 10) < cooldownMs) return;
    } catch (e) {
      /* localStorage unavailable (private mode etc.) – fall through and show once */
    }

    if (popup.querySelector('.wc-form__success')) {
      try {
        window.localStorage.setItem(storageKey, String(Date.now()));
      } catch (e) {}
      return;
    }

    const observer = new MutationObserver(() => {
      if (!popup.hasAttribute('open')) {
        try {
          window.localStorage.setItem(storageKey, String(Date.now()));
        } catch (e) {}
      }
    });
    observer.observe(popup, { attributes: true, attributeFilter: ['open'] });

    /* Solange Shopifys Cookie-Banner offen ist, wartet das Popup: erst die
       Cookie-Entscheidung, dann (kurz danach) der Newsletter. Shopify meldet die
       Entscheidung über das Ereignis „visitorConsentCollected“. */
    const consentBannerOpen = () => {
      const banner = document.getElementById('shopify-pc__banner');
      return !!(banner && getComputedStyle(banner).display !== 'none' && banner.getBoundingClientRect().height > 0);
    };

    let shown = false;
    let waitingForConsent = false;
    const trigger = () => {
      if (shown) return;
      if (consentBannerOpen()) {
        if (waitingForConsent) return;
        waitingForConsent = true;
        document.addEventListener('visitorConsentCollected', () => {
          window.setTimeout(() => {
            waitingForConsent = false;
            trigger();
          }, 2500);
        }, { once: true });
        return;
      }
      shown = true;
      popup.show();
    };

    const seconds = parseInt(popup.dataset.wcPopupSeconds, 10) || 8;
    const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;

    if (isCoarsePointer) {
      window.setTimeout(trigger, seconds * 1000);
    } else {
      const onExitIntent = (event) => {
        if (event.clientY <= 0) {
          document.removeEventListener('mouseout', onExitIntent);
          trigger();
        }
      };
      document.addEventListener('mouseout', onExitIntent);
    }
  }

  /* --- Signal-Grafik: verschiebbare Grenze der Gefahrenzone -------------
     Alle Werte in Bildkoordinaten (1536 × 1024). Die Grenze im Foto liegt auf
     Halsbandhöhe bei x = 952; sie lässt sich bis x = 752 an die Katze
     (Halsband x = 660) heranziehen. Je näher, desto höher die Stufe (1–8) und
     desto kräftiger die Wellen. Solange niemand zieht, läuft der normale Loop. */
  const SIGNAL_IMG_W = 1536;
  const SIGNAL_EDGE_X = 952;
  const SIGNAL_MAX_SHIFT = 200;
  const SIGNAL_CURVE = (dx) =>
    `M${1004 + dx} 0 C${975 + dx} 150 ${944 + dx} 330 ${952 + dx} 470 C${958 + dx} 610 ${985 + dx} 760 ${1095 + dx} 905 L${1180 + dx} 1024`;
  const SIGNAL_BAKED_BACK = 'L1180 1024 L1095 905 C985 760 958 610 952 470 C944 330 975 150 1004 0 Z';

  function initSignalSlider(root) {
    root.querySelectorAll('[data-wc-signal-visual]').forEach((visual) => {
      if (!once(visual, 'wcSlider')) return;
      const stage = visual.querySelector('[data-wc-signal-stage]');
      const handle = visual.querySelector('[data-wc-signal-handle]');
      const hit = visual.querySelector('[data-wc-signal-hit]');
      const zone = visual.querySelector('[data-wc-signal-zone]');
      const boundary = visual.querySelector('[data-wc-signal-boundary]');
      if (!stage || !handle || !zone || !boundary) return;

      let shift = 0; // 0 … SIGNAL_MAX_SHIFT (nach links)
      let frame = 0;

      const render = () => {
        frame = 0;
        const dx = -shift;
        const t = shift / SIGNAL_MAX_SHIFT;
        const level = Math.min(8, 1 + Math.round(t * 7));
        visual.style.setProperty('--wc-signal-x', `${((SIGNAL_EDGE_X + dx) / SIGNAL_IMG_W) * 100}%`);
        visual.style.setProperty('--wc-signal-gain', (0.35 + 0.65 * t).toFixed(3));
        boundary.setAttribute('transform', `translate(${dx} 0)`);
        zone.setAttribute('d', shift > 0.5 ? `${SIGNAL_CURVE(dx)} ${SIGNAL_BAKED_BACK}` : '');
        visual.classList.toggle('is-moved', shift > 0.5);
        visual.dataset.level = String(level);
        const percent = Math.round(t * 100);
        handle.setAttribute('aria-valuenow', String(percent));
        handle.setAttribute(
          'aria-valuetext',
          percent === 0
            ? handle.dataset.valueStart || ''
            : (handle.dataset.valueMoved || '').replace('[percent]', String(percent)).replace('[level]', String(level))
        );
      };
      const setShift = (value) => {
        shift = Math.max(0, Math.min(SIGNAL_MAX_SHIFT, value));
        if (!frame) frame = requestAnimationFrame(render);
      };
      const toShift = (clientX) => {
        const rect = stage.getBoundingClientRect();
        const x = ((clientX - rect.left) / rect.width) * SIGNAL_IMG_W;
        return SIGNAL_EDGE_X - x;
      };

      let pointerId = null;
      let grabOffset = 0;
      const onDown = (event) => {
        if (event.button !== undefined && event.button !== 0) return;
        pointerId = event.pointerId;
        grabOffset = shift - toShift(event.clientX);
        event.currentTarget.setPointerCapture(pointerId);
        visual.classList.add('is-dragging');
      };
      const onMove = (event) => {
        if (event.pointerId !== pointerId) return;
        setShift(toShift(event.clientX) + grabOffset);
      };
      const onUp = (event) => {
        if (event.pointerId !== pointerId) return;
        pointerId = null;
        visual.classList.remove('is-dragging');
      };
      [handle, hit].forEach((el) => {
        if (!el) return;
        el.addEventListener('pointerdown', onDown);
        el.addEventListener('pointermove', onMove);
        el.addEventListener('pointerup', onUp);
        el.addEventListener('pointercancel', onUp);
      });

      handle.addEventListener('keydown', (event) => {
        const step = SIGNAL_MAX_SHIFT / 10;
        const next = {
          ArrowLeft: shift + step,
          ArrowDown: shift + step,
          ArrowRight: shift - step,
          ArrowUp: shift - step,
          PageDown: shift + step * 3,
          PageUp: shift - step * 3,
          Home: 0,
          End: SIGNAL_MAX_SHIFT,
        }[event.key];
        if (next === undefined) return;
        event.preventDefault();
        setShift(next);
      });
    });
  }

  /* --- Footer: „Cookie-Einstellungen“ ------------------------------------
     Öffnet Shopifys Cookie-Einstellungen über die dokumentierte Customer-Privacy-
     Banner-API. Fallback: der Anker, den Shopifys Banner-Skript selbst abfängt. */
  function initCookieSettings(root) {
    root.querySelectorAll('[data-wc-cookie-settings]').forEach((btn) => {
      if (!once(btn, 'wcInit')) return;
      btn.setAttribute('aria-haspopup', 'dialog');
      btn.addEventListener('click', () => {
        const banner = window.privacyBanner;
        if (banner && typeof banner.showPreferences === 'function') {
          banner.showPreferences();
          return;
        }
        const link = document.createElement('a');
        link.href = '#shopifyReshowConsentBanner';
        link.hidden = true;
        document.body.appendChild(link);
        link.click();
        link.remove();
      });
    });
  }

  function initAll(root) {
    initHeader(root);
    initHero(root);
    initAppTabs(root);
    initInView(root);
    initCookieSettings(root);
    initSignalSlider(root);
    initSplineExplorer(root);
    initFaq(root);
    initReveal(root);
    initMobileCta();
    initNewsletterPopup(root);
  }

  const start = () => {
    initAll(document);
    scrollToFormStatus();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:reorder', () => initReveal(document));
  document.addEventListener('shopify:block:select', (event) => {
    const details = event.target && event.target.closest ? event.target.closest('details') : null;
    if (details) details.open = true;
    const tab = event.target && event.target.matches && event.target.matches('[data-wc-tab]')
      ? event.target
      : null;
    if (tab) tab.click();
  });
})();
